import { ReactElement, useMemo } from 'react'
import AreaLoadMeshElement from 'src/components/scene/AreaLoadMeshElement'
import FloorplanLineLoadsMesh from '../FloorplanLineLoadsMesh'
import FloorplanPointLoadsMesh from '../FloorplanPointLoadsMesh'

interface Props {
  loads: (DomainLoad | AreaLoad)[]
  /** Largest line load value; all line load widths scale proportionally to it. */
  maxLineLoadMagnitude: number
  /** Largest point load value; all point load circles scale proportionally to it. */
  maxPointLoadMagnitude: number
}

/** 2D area, point and line loads in the floorplan. */
const FloorplanLoadsMesh = ({
  loads,
  maxLineLoadMagnitude,
  maxPointLoadMagnitude,
}: Props): ReactElement => {
  const { areaLoads, pointLoads, lineLoads } = useMemo(
    () => ({
      areaLoads: loads.filter((load): load is AreaLoad => load.load_type === 'area-load'),
      pointLoads: loads.filter(
        (load): load is PointLoadWithDomain => load.load_type === 'point-load',
      ),
      lineLoads: loads.filter((load): load is LineLoadWithDomain => load.load_type === 'line-load'),
    }),
    [loads],
  )

  return (
    // 2D ELEMENTS positions above the slabs and roof slabs geometries
    <group position={[0, 0, 0.01]}>
      <group position={[0, 0, 0.01]}>
        {areaLoads.map(areaLoad => (
          <AreaLoadMeshElement key={areaLoad.guid} load={areaLoad} />
        ))}

        {pointLoads.length > 0 && (
          <group position={[0, 0, -1]}>
            <FloorplanPointLoadsMesh loads={pointLoads} maxMagnitude={maxPointLoadMagnitude} />
          </group>
        )}

        {lineLoads.length > 0 && (
          <group position={[0, 0, -1]}>
            <FloorplanLineLoadsMesh loads={lineLoads} maxMagnitude={maxLineLoadMagnitude} />
          </group>
        )}
      </group>
    </group>
  )
}

export default FloorplanLoadsMesh
